import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { getSenderOrders } from "services/order";
import PaginatedTable from "Components/shared/PaginatedTable";
import { debounce } from "utils/debounce";
import OrderSearch from "../../Order/orderSearch";
import {
  DEFAULT_PAGE,
  DEFAULT_ROWS_PER_PAGE,
  ORDER_FILTERS,
} from "constants/paginationConstants";
import { useLocation } from "react-router-dom";
import { orderStatus } from "enums/shared";
import formatDateForInput from "utils/formatDateForInput";

const SenderOrdersTab = () => {
  const params = useParams();
  const senderId = params.id || params.senderId;
  const { t } = useTranslation();
  const [page, setPage] = useState(DEFAULT_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const navigate = useNavigate();

  const location = useLocation();
  const queryState = location.state || {};

  const initialStatus = queryState.status
    ? Array.isArray(queryState.status)
      ? queryState.status[0]
      : Object.entries(orderStatus).find(
          ([, value]) => value.value === queryState.status
        )?.[0]
    : null;

  const [filters, setFilters] = useState({
    ...ORDER_FILTERS,
    status: initialStatus,
    pickupDate: null,
    dropoffDate: null,
  });

  // Create status options for the dropdown
  const statusOptions = Object.entries(orderStatus).map(([key, value]) => ({
    value: Number(key),
    label: t(value.value),
  }));

  // Fetch orders with filters
  const fetchSenderOrders = useCallback(async () => {
    const {
      status,
      orderNumber,
      receiverName,
      driverName,
      sortDescending,
      sortBy,
      pickupDate,
      dropoffDate,
    } = filters;

    return getSenderOrders(
      senderId,
      page + 1,
      rowsPerPage,
      status ? [status] : [],
      orderNumber,
      receiverName,
      driverName,
      sortDescending,
      sortBy,
      pickupDate,
      dropoffDate
    );
  }, [senderId, page, rowsPerPage, filters]);

  const { data, isLoading } = useQuery({
    queryKey: ["senderOrders", senderId, page, rowsPerPage, filters],
    queryFn: fetchSenderOrders,

    onSuccess: () => {
      setLastUpdated(Date.now());
    },
    select: (response) => ({
      rows: response.page.map((order) => ({
        ...order,
        creationDate: order.creationDate.split("T")[0],
        status: t(orderStatus[order.status]?.value || "Unknown"),
        driverName: order.driverName || t("-"),
        pickupDateTime: order.preferredPickUpDate
          ? formatDateForInput(order.preferredPickUpDate)
          : "-",
        dropOffDateTime: order.preferredDeliveryDate
          ? formatDateForInput(order.preferredDeliveryDate)
          : "-",
      })),
      totalRecords: response.totalRecords,
    }),

    enabled: !!senderId,
    keepPreviousData: true,
  });

  const columns = [
    { id: "orderNumber", label: t("orderNumber"), sortable: true },
    { id: "receiverName", label: t("reciverName"), sortable: true },
    { id: "driverName", label: t("driverName"), sortable: true },
    { id: "pickupDateTime", label: t("pickupDateTime"), sortable: true },
    { id: "dropOffDateTime", label: t("dropOffDateTime"), sortable: true },
    { id: "creationDate", label: t("creationDate"), sortable: true },
    { id: "status", label: t("status"), sortable: true, name: "orderStatus" },
  ];

  const handleChangePage = (_, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Sorting handler
  const handleSortChange = (column, isDescending) => {
    setFilters((prev) => ({
      ...prev,
      sortBy: column,
      sortDescending: isDescending,
    }));
  };

  // Debounced filter updates
  const debouncedUpdateFilters = useMemo(
    () =>
      debounce((newFilters) => {
        setFilters((prev) => ({ ...prev, ...newFilters }));
        setPage(0);
      }, 500),
    []
  );

  const setDebouncedFilters = useCallback(
    (newFilters) => {
      debouncedUpdateFilters(newFilters);
    },
    [debouncedUpdateFilters]
  );

  // Search handler
  const handleSearch = (value, columnId) => {
    if (["orderNumber", "receiverName", "driverName"].includes(columnId)) {
      setDebouncedFilters({ [columnId]: value });
    }
  };

  // Status change handler
  const handleStatusChange = (value) => {
    setFilters((prev) => ({ ...prev, status: value }));
  };

  const handleDateChange = (key, date) => {
    const updatedFilters = { ...filters, [key]: date };
    setFilters(updatedFilters);
  };

  const handleRowClick = (row) => {
    navigate(`/orders/${row.id}`);
  };

  const [lastUpdated, setLastUpdated] = useState(Date.now());

  return (
    <>
      {
        <OrderSearch
          fetchDataHandler={setDebouncedFilters}
          initialFilters={{ status: initialStatus }}
          lastUpdated={lastUpdated}
        />
      }{" "}
      <PaginatedTable
        columns={columns}
        rows={data?.rows || []}
        isLoading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        totalRecords={data?.totalRecords || 0}
        showActions={true}
        filters={filters}
        onSearch={handleSearch}
        handleStatusChange={handleStatusChange}
        ordrStatusOptions={statusOptions}
        onSortChange={handleSortChange}
        handleDateChange={handleDateChange}
        onRowClick={handleRowClick}
      />
    </>
  );
};

export default SenderOrdersTab;
